import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchBrands} from 'api';
import {ReactComponent as FavoriteIcon} from 'assets/svg/favorite.svg';
import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {Page} from 'components/common/Page';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {AsyncSelect, Select} from 'components/ui-kit/Select';
import {ProductGroups} from 'core/constants/productGroups';
import {PRODUCT_ACTIVE_STATUS_COLORS, PRODUCT_GTIN_STATUS_COLORS} from 'core/constants/statuses';
import {ROUTE} from 'core/routes';
import {listState$, showConfigBlockChanged} from 'models/registries/gtinsGrid/list';
import {favoritesSubmitted, favoriteToggled, tableState$} from 'models/registries/gtinsGrid/table';
import {editGtinFx, ProductsGate} from 'models/registries/gtinsGrid/upload';
import {breakpoint, colors} from 'theme';
import {UserPermission} from 'types';
import {
    ActiveStatuses,
    GtinsAccessors,
    GtinsGridFormattedRow,
    GtinsGridRawRow,
    GtinStatuses,
    PackageTypes,
} from 'types/gtins';
import {useMatchMedia, usePermission} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {ProductStatusesOptions} from 'utils/packageTypes-mappers';
import {mapArrayToI18nOptions, mapEnumToI18nOptions} from 'utils/select';

import {GtinsUploadForm} from '../GtinsUploadForm';
import {GtinsUploadList} from '../GtinsUploadList';

import {Controls} from './Controls';
import {GridsNavigation} from './GridsNavigation';
import {useGtinsModeFilter} from './hooks/useGtinsModeFilter';
import {GtinsGridList} from './List';
import {
    FavoriteButton,
    FavoriteCell,
    GtinEditLink,
    HeaderControls,
    HeaderControlsTop,
    QuickFilterActions,
    QuickFilterButton,
    QuickFilterClear,
    QuickFilterControls,
    QuickFiltersLabel,
    QuickFiltersRow,
    StatusWrapperText,
} from './styled';
import {GtinsGridTable} from './Table';

const getConfig = (t: Function, isFavoritesRoute = false) => {
    const config = [
        // {
        //     id: GtinsAccessors.isFavorite,
        //     header: '',
        //     hideHeader: true,
        //     isSortable: false,
        //     ignoreConfig: true,
        //     width: 50,
        // },
        {
            id: GtinsAccessors.productGroupCode,
            header: t('pages.registries.gtins.productGroup'),
            filter: (
                <Select
                    options={mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups')}
                    isClearable
                    isMulti
                />
            ),
            isSortable: false,
            ignoreConfig: true,
            width: 150,
        },
        {
            id: GtinsAccessors.name,
            header: t('pages.registries.gtins.name'),
            filter: <Input placeholder={t('Enter value')} />,
            isSortable: true,
        },
        {
            id: GtinsAccessors.productVersion,
            header: t('pages.registries.gtins.versionNumber'),
            isSortable: true,
            width: 140,
        },
        {
            id: GtinsAccessors.updatedAt,
            header: t('pages.registries.gtins.versionDate'),
            isSortable: true,
            filter: <DatePicker />,
            width: 190,
        },
        {
            id: GtinsAccessors.gtin,
            header: t('pages.registries.gtins.gtin'),
            filter: <Input placeholder={t('Enter value')} />,
            isSortable: true,
            width: 170,
        },
        {
            id: GtinsAccessors.brand,
            header: t('pages.registries.gtins.brand'),
            filter: (
                <AsyncSelect
                    name={'trademark-selector'}
                    isSearchable={false}
                    defaultOptions
                    loadOptions={async () => {
                        const value = await fetchBrands();
                        return value
                            .sort((a: string, b: string) => a.localeCompare(b))
                            .map((item: string) => ({
                                value: item,
                                label: item,
                            }));
                    }}
                    isClearable
                />
            ),
            width: 150,
        },
        {
            id: GtinsAccessors.packageType,
            header: t('pages.registries.gtins.packageType'),
            filter: (
                <Select
                    options={mapArrayToI18nOptions(
                        [PackageTypes.UNIT, PackageTypes.GROUP].map((value) => ({
                            key: value,
                            value,
                        })),
                        'dictionaries.products.packageType',
                    )}
                />
            ),
            isSortable: true,
            width: 180,
        },
        {
            id: GtinsAccessors.status,
            header: t('pages.registries.gtins.status'),
            filter: (
                <Select
                    options={mapArrayToI18nOptions(
                        ProductStatusesOptions,
                        'dictionaries.productStatuses',
                    )}
                />
            ),
            width: 150,
        },
    ];

    if (isFavoritesRoute) {
        return config.filter(
            (item) => ![GtinsAccessors.productVersion, GtinsAccessors.updatedAt].includes(item.id),
        );
    }

    return config;
};

export const GtinsGridPage = () => {
    useGate(ProductsGate);
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const hasViewPermission = usePermission([UserPermission.GTIN_DETAIL_VIEW]);
    const {showConfigBlock} = useStore(listState$);
    const {favoritesPending} = useStore(tableState$);
    const [selectedGtins, setSelectedGtins] = React.useState<string[]>([]);
    const [resetSelectedRows, setResetSelectedRows] = React.useState(false);
    const {
        activeModeFilter,
        modeFilterOptions,
        setModeFilter,
        showModeFilters,
    } = useGtinsModeFilter({isMobile});

    const isAllProductsRoute = useMemo(() => location.pathname === ROUTE.REGISTRIES_GTIN_ALL, [
        location.pathname,
    ]);
    const isFavoritesRoute = useMemo(() => location.pathname === ROUTE.REGISTRIES_GTIN_FAVORITES, [
        location.pathname,
    ]);
    const showBulkFavoritesButton = [
        ROUTE.REGISTRIES_GTIN_MY_CREATED,
        ROUTE.REGISTRIES_GTIN_ALL,
        ROUTE.REGISTRIES_GTIN_FAVORITES,
    ].includes(location.pathname as ROUTE);
    const configFunc = React.useCallback((t: TFunction) => getConfig(t, isFavoritesRoute), [
        isFavoritesRoute,
    ]);

    const onToggleFavorite = (gtin: string, currentStatus?: boolean) => {
        if (!gtin) return;
        favoriteToggled({gtin, currentStatus});
    };

    const getColors = (status: GtinStatuses) => ({
        color:
            PRODUCT_GTIN_STATUS_COLORS[status ?? 'DEFAULT'] ?? PRODUCT_ACTIVE_STATUS_COLORS.DEFAULT,
    });

    const convertData = (
        data: GtinsGridRawRow[] | undefined,
        t: TFunction,
    ): GtinsGridFormattedRow[] => {
        if (!data) return [];

        const getColorsActive = (status: boolean) => ({
            color:
                PRODUCT_ACTIVE_STATUS_COLORS[
                    status === undefined
                        ? 'DEFAULT'
                        : status
                        ? ActiveStatuses.ACTIVE
                        : ActiveStatuses.INACTIVE
                ],
        });

        return data.map((elem) => ({
            productId: elem.productId,
            rawGtin: elem.gtin,
            rawIsFavorite: elem.isFavorite,
            [GtinsAccessors.isFavorite]:
                isAllProductsRoute && elem.status !== GtinStatuses.PUBLISHED ? null : (
                    <FavoriteCell>
                        <FavoriteButton
                            type="button"
                            isActive={elem.isFavorite}
                            onClick={() => onToggleFavorite(elem.gtin, elem.isFavorite)}
                        >
                            <FavoriteIcon />
                        </FavoriteButton>
                    </FavoriteCell>
                ),
            [GtinsAccessors.productGroupCode]: handleNil(elem.productGroupCode, (value) => (
                <ProductGroupIcon value={value} label />
            )),
            [GtinsAccessors.name]: handleNil(elem.productName),
            [GtinsAccessors.gtin]: handleNil(elem.gtin, (value) => {
                const gtin = value ? value.toString().trim() : '';
                const {productId} = elem;
                return hasViewPermission ? (
                    <GtinEditLink onClick={() => onEdit({gtin, productId})}>{gtin}</GtinEditLink>
                ) : (
                    <div>{gtin}</div>
                );
            }),
            [GtinsAccessors.brand]: handleNil(elem.productTrademark),
            // [GtinsAccessors.manufacturer]: handleNil(elem.manufacturerName),
            [GtinsAccessors.packageType]: handleNil(elem.packageType, (value) =>
                t('dictionaries.products.packageType.' + value),
            ),
            [GtinsAccessors.status]: handleNil(elem.status, (value) => (
                <StatusWrapper {...getColors(value)}>
                    {t(`dictionaries.productStatuses.${value}`)}
                </StatusWrapper>
            )),
            [GtinsAccessors.isActive]: handleNil(elem.isActive, (value) =>
                value ? (
                    <StatusWrapperText {...getColorsActive(value)}>
                        {t('pages.registries.activeStatus.active')}
                    </StatusWrapperText>
                ) : (
                    <StatusWrapperText {...getColorsActive(value)}>
                        {t('pages.registries.activeStatus.inactive')}
                    </StatusWrapperText>
                ),
            ),
            [GtinsAccessors.productVersion]: handleNil(elem.productVersion),
            [GtinsAccessors.updatedAt]: handleNil(elem.updatedAt, (value: string) =>
                formatDate(value, 'dd.MM.yyyy'),
            ),
        }));
    };

    const onEdit = async ({
        gtin,
        productId,
    }: {
        gtin: string | number;
        productId?: string | null;
    }) => {
        try {
            await editGtinFx({gtin, productId});
        } catch (error) {
            console.error(error);
        }
    };

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : null;

    const headerControls = (
        <HeaderControls>
            <HeaderControlsTop>
                <Controls />
                <GridsNavigation />
            </HeaderControlsTop>
            {(showModeFilters || showBulkFavoritesButton) && (
                <QuickFiltersRow>
                    <QuickFilterControls>
                        {showModeFilters && (
                            <>
                                <QuickFiltersLabel>
                                    {t('pages.registries.gtins.quickFilters.filters')}
                                </QuickFiltersLabel>
                                {modeFilterOptions.map(({label, value}) => (
                                    <QuickFilterButton
                                        key={label}
                                        type="button"
                                        isActive={activeModeFilter === value}
                                        onClick={() =>
                                            setModeFilter(
                                                activeModeFilter === value ? undefined : value,
                                            )
                                        }
                                    >
                                        {label}
                                    </QuickFilterButton>
                                ))}
                                <QuickFilterClear
                                    type="button"
                                    onClick={() => setModeFilter(undefined)}
                                >
                                    {t('pages.registries.gtins.quickFilters.clearAll')}
                                </QuickFilterClear>
                            </>
                        )}
                    </QuickFilterControls>
                    <QuickFilterActions>
                        {showBulkFavoritesButton && (
                            <Button
                                size="sm"
                                variant={
                                    isFavoritesRoute
                                        ? ButtonVariant.Secondary
                                        : ButtonVariant.Normal
                                }
                                disabled={!selectedGtins.length}
                                isLoading={favoritesPending}
                                onClick={() => {
                                    if (!selectedGtins.length) return;

                                    favoritesSubmitted({
                                        gtins: selectedGtins,
                                        status: !isFavoritesRoute,
                                    });
                                    setSelectedGtins([]);
                                    setResetSelectedRows((value) => !value);
                                }}
                            >
                                {isFavoritesRoute
                                    ? t('pages.registries.gtins.favorites.remove')
                                    : t('pages.registries.gtins.favorites.add')}
                            </Button>
                        )}
                    </QuickFilterActions>
                </QuickFiltersRow>
            )}
        </HeaderControls>
    );

    return (
        <Page title={t('menu.Registries')} controls={pageHeaderControls}>
            {!isMobile && (
                <GtinsGridTable
                    configFunc={configFunc}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    onSelectedGtinsChange={setSelectedGtins}
                    resetSelectedRows={resetSelectedRows}
                    // exportConfig={exportConfig}
                />
            )}
            {isMobile && (
                <GtinsGridList
                    configFunc={configFunc}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    showConfigBlock={showConfigBlock}
                    // exportConfig={exportConfig}
                />
            )}
            <GtinsUploadList />
            <GtinsUploadForm />
        </Page>
    );
};
